<script setup lang="ts">
import { onMounted, ref } from 'vue';
import TaskAccordion from './TaskAccordion.vue';
defineProps<{ collapsible?: boolean }>();
type Notification = {
  paymentAttemptId: string;
  boxId?: string;
  deliveryRequestId?: string;
  kind?: string;
  amountMinor: number;
  currency: string;
  occurredAt: string;
};
const notifications = ref<Notification[]>([]),
  busy = ref(false),
  error = ref('');
async function load(): Promise<void> {
  if (busy.value) return;
  busy.value = true;
  error.value = '';
  try {
    const response = await globalThis.fetch('/admin/api/payment-notifications', {
      credentials: 'include',
    });
    if (!response.ok) throw new Error();
    const body = await response.json();
    if (!Array.isArray(body.notifications)) throw new Error();
    notifications.value = body.notifications;
  } catch {
    error.value = 'Alertes de paiement indisponibles.';
  } finally {
    busy.value = false;
  }
}
onMounted(load);
</script>
<template>
  <component
    :is="collapsible ? TaskAccordion : 'section'"
    title="Paiements à vérifier"
    :count="notifications.length"
    :retryable="true"
    @retry="load"
    :loading="busy"
    :error="error"
    :class="{ 'payment-notifications': !collapsible }"
    aria-label="Paiements refusés et à vérifier"
  >
    <h3 v-if="!collapsible">Paiements refusés et à vérifier</h3>
    <p>
      Notifications dans l’application. Aucun e-mail ni notification téléphone n’est envoyé.
    </p>
    <button type="button" :disabled="busy" @click="load">
      Actualiser les alertes de paiement
    </button>
    <p v-if="error" role="alert">{{ error }}</p>
    <p v-else-if="busy">Chargement…</p>
    <p v-else-if="!notifications.length">Aucune alerte de paiement en attente.</p>
    <ul v-if="!error && !busy">
      <li v-for="n in notifications" :key="n.paymentAttemptId">
        <strong
          >{{
            n.kind === 'ORDER_BANK_REVIEW_REQUIRED'
              ? 'Traitement bancaire de commande à vérifier'
              : n.kind === 'AUTHORIZATION_REVIEW_REQUIRED'
                ? 'Empreinte insuffisante ou expirée · préparation suspendue'
                : n.kind === 'REFUND_REVIEW_REQUIRED'
                  ? 'Remboursement à vérifier'
                  : n.kind === 'CHECKOUT_REVIEW_REQUIRED'
                    ? 'Paiement incertain · rapprochement requis'
                    : n.kind === 'SUPPLEMENT_PAYMENT_FAILED'
                      ? 'Complément refusé'
                      : 'Paiement refusé'
          }}{{ n.boxId ? ' · ' + n.boxId : '' }}</strong
        ><br />{{ (n.amountMinor / 100).toFixed(2) }} {{ n.currency }} ·
        {{ new Date(n.occurredAt).toLocaleString('fr-FR') }}<br />Référence
        {{ n.paymentAttemptId }}
        <span v-if="n.deliveryRequestId"> · Commande {{ n.deliveryRequestId }}</span>
      </li>
    </ul>
  </component>
</template>
<style scoped>
.payment-notifications {
  padding: 1rem;
  margin: 1rem 0;
  border: 2px solid #eab308;
  border-radius: 1rem;
  background: #fffbeb;
  overflow-wrap: anywhere;
}
button {
  min-height: 44px;
  padding: 0.7rem;
  background: #20214f;
  color: white;
  border-radius: 0.6rem;
}
li {
  margin-top: 1rem;
}
</style>
